import { useEffect, useMemo, useState } from 'react';
import { notebookAPI, disciplinaryAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { BookLock, Search, Trash2, NotebookPen, Clock, User, Download, History, Pencil, Paperclip, ShieldCheck, FileClock, Megaphone, Scale } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

const CATEGORY_STYLES = {
  'Positive Performance': 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
  'Behaviour Concern': 'bg-red-500/20 text-red-400 border-red-500/30',
  'Work Performance': 'bg-indigo-500/20 text-indigo-400 border-indigo-500/30',
  'Attendance / Conduct': 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  'General Management': 'bg-slate-500/20 text-slate-300 border-slate-500/30',
};

function formatWhen(iso) {
  try {
    return new Date(iso).toLocaleString('en-ZA', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function formatDisplayDate(iso) {
  try {
    return new Date(iso).toLocaleDateString('en-ZA', {
      day: '2-digit', month: 'short', year: 'numeric',
    });
  } catch {
    return iso;
  }
}

export default function EmployeeNotebook() {
  const [employees, setEmployees] = useState([]);
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [notes, setNotes] = useState([]);
  const [loadingNotes, setLoadingNotes] = useState(false);
  const [category, setCategory] = useState('');
  const [content, setContent] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [exporting, setExporting] = useState(false);

  // Historic paper-record capture
  const [entryType, setEntryType] = useState('current'); // 'current' | 'historic'
  const [historicDate, setHistoricDate] = useState('');
  const [sourceReference, setSourceReference] = useState('');
  const [attachment, setAttachment] = useState(null); // { name, data }
  const [confirmHistoric, setConfirmHistoric] = useState(false);

  // Edit + audit
  const [editTarget, setEditTarget] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [editCategory, setEditCategory] = useState('');
  const [editReason, setEditReason] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  const [auditTarget, setAuditTarget] = useState(null);
  const [auditData, setAuditData] = useState(null);
  const [loadingAudit, setLoadingAudit] = useState(false);

  // Disciplinary-case shortcut (raise a case from a behaviour-concern note)
  const [caseNote, setCaseNote] = useState(null);
  const [caseForm, setCaseForm] = useState({ case_type: 'Verbal Warning', description: '' });
  const [creatingCase, setCreatingCase] = useState(false);

  const today = new Date().toISOString().slice(0, 10);
  const GENERAL = { employee_id: '__GENERAL__', full_name: 'General / Group', job_title: 'Applies to all employees', department: 'Company-wide' };
  const isGeneral = selected?.employee_id === '__GENERAL__';

  useEffect(() => {
    notebookAPI.getEmployees().then((r) => setEmployees(r.data)).catch(() => toast.error('Failed to load employees'));
    notebookAPI.getCategories().then((r) => setCategories(r.data)).catch((e) => console.error('Failed to load categories', e));
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return employees;
    return employees.filter((e) =>
      e.full_name?.toLowerCase().includes(q) ||
      e.job_title?.toLowerCase().includes(q) ||
      e.department?.toLowerCase().includes(q));
  }, [employees, search]);

  const loadNotes = async (emp) => {
    setSelected(emp);
    setLoadingNotes(true);
    try {
      const r = await notebookAPI.getNotes(emp.employee_id);
      setNotes(r.data);
    } catch {
      toast.error('Failed to load notes');
    } finally {
      setLoadingNotes(false);
    }
  };

  const refreshCounts = async () => {
    try {
      const r = await notebookAPI.getEmployees();
      setEmployees(r.data);
    } catch (e) {
      console.error('Failed to refresh note counts', e);
    }
  };

  const resetForm = () => {
    setContent('');
    setCategory('');
    setEntryType('current');
    setHistoricDate('');
    setSourceReference('');
    setAttachment(null);
  };

  const doSaveNote = async () => {
    setSaving(true);
    try {
      await notebookAPI.addNote({
        employee_id: isGeneral ? null : selected.employee_id,
        scope: isGeneral ? 'general' : 'employee',
        category,
        content: content.trim(),
        entry_type: entryType,
        historic_date: entryType === 'historic' ? historicDate : null,
        source_reference: sourceReference.trim() || null,
        attachment_name: attachment?.name || null,
        attachment_data: attachment?.data || null,
      });
      toast.success(isGeneral ? 'General note posted to all employees' : (entryType === 'historic' ? 'Historic record captured' : 'Note added'));
      resetForm();
      await loadNotes(selected);
      refreshCounts();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add note');
    } finally {
      setSaving(false);
      setConfirmHistoric(false);
    }
  };

  const handleAdd = async () => {
    if (!selected) return;
    if (!category) { toast.error('Please choose a category'); return; }
    if (!content.trim()) { toast.error('Please enter a note'); return; }
    if (entryType === 'historic') {
      if (!historicDate) { toast.error('Historic Record Date is required'); return; }
      if (historicDate > today) { toast.error('Historic date cannot be in the future'); return; }
      setConfirmHistoric(true);  // show mandatory confirmation before saving
      return;
    }
    await doSaveNote();
  };

  const handleAttachment = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Attachment must be under 5MB');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setAttachment({ name: file.name, data: reader.result });
    reader.readAsDataURL(file);
  };

  const openEdit = (n) => {
    setEditTarget(n);
    setEditContent(n.content);
    setEditCategory(n.category);
    setEditReason('');
  };

  const handleSaveEdit = async () => {
    if (!editTarget) return;
    if (!editContent.trim()) { toast.error('Note content is required'); return; }
    setSavingEdit(true);
    try {
      await notebookAPI.editNote(editTarget.id, {
        content: editContent.trim(),
        category: editCategory,
        edit_reason: editReason.trim() || null,
      });
      toast.success('Entry updated (original preserved in audit)');
      setEditTarget(null);
      await loadNotes(selected);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update entry');
    } finally {
      setSavingEdit(false);
    }
  };

  const openAudit = async (n) => {
    setAuditTarget(n);
    setAuditData(null);
    setLoadingAudit(true);
    try {
      const r = await notebookAPI.getAudit(n.id);
      setAuditData(r.data);
    } catch (e) {
      toast.error('Failed to load audit trail');
    } finally {
      setLoadingAudit(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await notebookAPI.deleteNote(deleteTarget.id);
      toast.success('Note deleted (audit recorded)');
      setDeleteTarget(null);
      await loadNotes(selected);
      refreshCounts();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete note');
    }
  };

  const openCase = (n) => {
    setCaseForm({ case_type: 'Verbal Warning', description: n.content });
    setCaseNote(n);
  };

  const handleCreateCase = async () => {
    if (!caseNote || !selected) return;
    if (!caseForm.case_type || !caseForm.description.trim()) {
      toast.error('Case type and description are required');
      return;
    }
    setCreatingCase(true);
    try {
      await disciplinaryAPI.create({
        employee_id: selected.employee_id,
        case_type: caseForm.case_type,
        description: caseForm.description.trim(),
        date: new Date().toISOString().slice(0, 10),
        outcome: '',
        related_note_id: caseNote.id,
      });
      toast.success('Disciplinary case opened from note');
      setCaseNote(null);
      await loadNotes(selected);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to open disciplinary case');
    } finally {
      setCreatingCase(false);
    }
  };

  const handleExport = async () => {    if (!selected) return;
    setExporting(true);
    try {
      const res = await notebookAPI.exportPdf(selected.employee_id);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `notebook_${(selected.full_name || selected.employee_id).replace(/\s+/g, '_')}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Notebook exported');
    } catch (e) {
      toast.error('Failed to export notebook');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="employee-notebook-page">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center shadow-lg shadow-orange-500/20">
          <BookLock className="w-5 h-5 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-foreground">Employee Notebook</h1>
          <p className="text-sm text-muted-foreground">Master-only internal notes &amp; history per employee</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-6">
        {/* Employee list */}
        <div className="rounded-xl border border-white/5 bg-card/40">
          <div className="p-3 border-b border-white/5">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                data-testid="notebook-employee-search"
                placeholder="Search employees…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>
          <ScrollArea className="h-[62vh]">
            <div className="p-2 space-y-1">
              {/* General / Group notebook — populates every employee's notes */}
              <button
                data-testid="notebook-employee-__GENERAL__"
                onClick={() => loadNotes(GENERAL)}
                className={cn(
                  'w-full text-left px-3 py-2.5 rounded-lg transition-colors flex items-center gap-2 mb-1',
                  isGeneral ? 'bg-amber-500/15 border border-amber-500/40' : 'hover:bg-secondary/50 border border-amber-500/20',
                )}
              >
                <Megaphone className="w-4 h-4 text-amber-400 shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-foreground truncate">General / Group</p>
                  <p className="text-xs text-muted-foreground truncate">Applies to all employees</p>
                </div>
              </button>
              {filtered.map((emp) => (
                <button
                  key={emp.employee_id}
                  data-testid={`notebook-employee-${emp.employee_id}`}
                  onClick={() => loadNotes(emp)}
                  className={cn(
                    'w-full text-left px-3 py-2.5 rounded-lg transition-colors flex items-center justify-between gap-2',
                    selected?.employee_id === emp.employee_id ? 'bg-orange-500/15 border border-orange-500/30' : 'hover:bg-secondary/50 border border-transparent',
                  )}
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">{emp.full_name}</p>
                    <p className="text-xs text-muted-foreground truncate">{emp.job_title || '—'}</p>
                    {emp.status === 'Terminated' && (
                      <Badge variant="outline" className="mt-1 border bg-red-500/15 text-red-300 border-red-500/30 text-[10px] px-1 py-0" data-testid={`notebook-terminated-${emp.employee_id}`}>Terminated</Badge>
                    )}
                  </div>
                  {emp.note_count > 0 && (
                    <Badge variant="secondary" className="shrink-0">{emp.note_count}</Badge>
                  )}
                </button>
              ))}
              {filtered.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-8">No employees found</p>
              )}
            </div>
          </ScrollArea>
        </div>

        {/* Notebook detail */}
        <div className="rounded-xl border border-white/5 bg-card/40 min-h-[62vh]">
          {!selected ? (
            <div className="h-full flex flex-col items-center justify-center text-center p-10 text-muted-foreground">
              <NotebookPen className="w-10 h-10 mb-3 opacity-40" />
              <p>Select an employee to view and add notebook entries.</p>
            </div>
          ) : (
            <div className="flex flex-col h-full">
              <div className="p-5 border-b border-white/5 flex items-center justify-between gap-3">
                <div>
                  <h2 className="text-lg font-semibold text-foreground" data-testid="notebook-selected-name">{selected.full_name}</h2>
                  <p className="text-sm text-muted-foreground">{selected.job_title || '—'} · {selected.department || '—'}</p>
                </div>
                {!isGeneral && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleExport}
                    disabled={exporting || notes.length === 0}
                    data-testid="notebook-export-btn"
                    className="shrink-0"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    {exporting ? 'Exporting…' : 'Export PDF'}
                  </Button>
                )}
              </div>

              {/* Add note */}
              <div className="p-5 border-b border-white/5 space-y-3">
                {/* Entry type toggle */}
                <div className="flex gap-2" data-testid="notebook-entry-type">
                  <Button
                    type="button"
                    variant={entryType === 'current' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setEntryType('current')}
                    data-testid="notebook-entry-current"
                    className={cn(entryType === 'current' && 'bg-orange-600 hover:bg-orange-700')}
                  >
                    <NotebookPen className="w-4 h-4 mr-2" /> Current Entry
                  </Button>
                  <Button
                    type="button"
                    variant={entryType === 'historic' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setEntryType('historic')}
                    data-testid="notebook-entry-historic"
                    className={cn(entryType === 'historic' && 'bg-amber-600 hover:bg-amber-700')}
                  >
                    <FileClock className="w-4 h-4 mr-2" /> Historic Paper Record
                  </Button>
                </div>

                {entryType === 'historic' && (
                  <div className="space-y-1" data-testid="notebook-historic-date-wrap">
                    <Label htmlFor="historic-date">Historic Record Date <span className="text-red-400">*</span></Label>
                    <Input
                      id="historic-date"
                      type="date"
                      max={today}
                      value={historicDate}
                      onChange={(e) => setHistoricDate(e.target.value)}
                      className="w-full"
                      data-testid="notebook-historic-date"
                    />
                    <p className="text-xs text-amber-300/80">This date will be shown in the notebook. The system-entry date is stored securely in the background audit only.</p>
                  </div>
                )}

                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger data-testid="notebook-category-select" className="w-full">
                    <SelectValue placeholder="Select note category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((c) => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Textarea
                  data-testid="notebook-note-content"
                  placeholder="Write a note… (positive performance, behaviour concern, attendance observation, etc.)"
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  rows={3}
                />
                <Input
                  data-testid="notebook-source-reference"
                  placeholder="Optional reference (e.g. transferred from physical file, box 3)"
                  value={sourceReference}
                  onChange={(e) => setSourceReference(e.target.value)}
                />
                <div className="flex items-center justify-between gap-3">
                  <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer hover:text-foreground">
                    <Paperclip className="w-4 h-4" />
                    <span data-testid="notebook-attachment-label">{attachment ? attachment.name : 'Attach supporting scan (optional)'}</span>
                    <input type="file" className="hidden" onChange={handleAttachment} data-testid="notebook-attachment-input" accept="image/*,.pdf" />
                  </label>
                  {attachment && (
                    <Button variant="ghost" size="sm" onClick={() => setAttachment(null)} data-testid="notebook-attachment-clear">Remove</Button>
                  )}
                </div>
                <div className="flex justify-end">
                  <Button data-testid="notebook-add-note-btn" onClick={handleAdd} disabled={saving} className="bg-orange-600 hover:bg-orange-700">
                    {saving ? 'Saving…' : (entryType === 'historic' ? 'Capture Historic Record' : 'Add Note')}
                  </Button>
                </div>
              </div>

              {/* History */}
              <ScrollArea className="flex-1">
                <div className="p-5 space-y-3" data-testid="notebook-history">
                  {loadingNotes ? (
                    <p className="text-sm text-muted-foreground">Loading…</p>
                  ) : notes.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-8">No notes yet for this employee.</p>
                  ) : (
                    notes.map((n) => (
                      <div key={n.id} data-testid={`notebook-note-${n.id}`} className="rounded-lg border border-white/5 bg-secondary/20 p-4">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-center gap-2 flex-wrap">
                            <Badge variant="outline" className={cn('border', CATEGORY_STYLES[n.category] || '')}>{n.category}</Badge>
                            {n.scope === 'general' && (
                              <Badge variant="outline" className="border bg-amber-500/15 text-amber-300 border-amber-500/30" data-testid={`notebook-general-badge-${n.id}`}>
                                <Megaphone className="w-3 h-3 mr-1" /> General
                              </Badge>
                            )}
                            {n.disciplinary_case_id && (
                              <Badge variant="outline" className="border bg-red-500/15 text-red-300 border-red-500/30" data-testid={`notebook-case-badge-${n.id}`}>
                                <Scale className="w-3 h-3 mr-1" /> Case: {n.disciplinary_case_type}
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-1">
                            {n.category === 'Behaviour Concern' && n.scope !== 'general' && !n.disciplinary_case_id && (
                              <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-amber-400" data-testid={`notebook-create-case-${n.id}`} onClick={() => openCase(n)} title="Open disciplinary case from this note">
                                <Scale className="w-4 h-4" />
                              </Button>
                            )}
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-sky-400" data-testid={`notebook-audit-${n.id}`} onClick={() => openAudit(n)} title="View audit trail">
                              <ShieldCheck className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-primary" data-testid={`notebook-edit-${n.id}`} onClick={() => openEdit(n)} title="Edit entry">
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-red-400" data-testid={`notebook-delete-${n.id}`} onClick={() => setDeleteTarget(n)} title="Withdraw entry">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                        <p className="text-sm text-foreground mt-2 whitespace-pre-wrap">{n.content}</p>
                        {n.source_reference && (
                          <p className="text-xs text-muted-foreground/80 mt-2 italic" data-testid={`notebook-source-${n.id}`}>Ref: {n.source_reference}</p>
                        )}
                        {n.attachment_name && (
                          <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1"><Paperclip className="w-3 h-3" />{n.attachment_name}</p>
                        )}
                        <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1" data-testid={`notebook-date-${n.id}`}><Clock className="w-3 h-3" />{formatDisplayDate(n.display_date)}</span>
                          <span className="flex items-center gap-1"><User className="w-3 h-3" />{n.created_by_name}</span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </ScrollArea>
            </div>
          )}
        </div>
      </div>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="notebook-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Withdraw this entry?</AlertDialogTitle>
            <AlertDialogDescription>
              The entry will be archived (withdrawn) and removed from the notebook, but it is never permanently deleted — the full audit history is preserved. Master-only.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="notebook-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700" data-testid="notebook-delete-confirm">
              Withdraw
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Historic capture confirmation */}
      <AlertDialog open={confirmHistoric} onOpenChange={(o) => !o && setConfirmHistoric(false)}>
        <AlertDialogContent data-testid="notebook-historic-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Capture historic physical record</AlertDialogTitle>
            <AlertDialogDescription>
              You are capturing information from a historic physical employee record. The date selected
              ({historicDate ? formatDisplayDate(`${historicDate}T00:00:00`) : '—'}) will be the date displayed in the
              employee notebook. The actual system-entry date will be retained securely in the background audit record.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="notebook-historic-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doSaveNote} disabled={saving} className="bg-amber-600 hover:bg-amber-700" data-testid="notebook-historic-confirm-save">
              {saving ? 'Saving…' : 'Confirm & Save'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit entry */}
      <Dialog open={!!editTarget} onOpenChange={(o) => !o && setEditTarget(null)}>
        <DialogContent data-testid="notebook-edit-dialog">
          <DialogHeader>
            <DialogTitle>Edit notebook entry</DialogTitle>
            <DialogDescription>The original content is preserved permanently in the background audit trail.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Select value={editCategory} onValueChange={setEditCategory}>
              <SelectTrigger data-testid="notebook-edit-category"><SelectValue /></SelectTrigger>
              <SelectContent>
                {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
            <Textarea rows={4} value={editContent} onChange={(e) => setEditContent(e.target.value)} data-testid="notebook-edit-content" />
            <Input placeholder="Reason for edit (optional, stored in audit)" value={editReason} onChange={(e) => setEditReason(e.target.value)} data-testid="notebook-edit-reason" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditTarget(null)}>Cancel</Button>
            <Button onClick={handleSaveEdit} disabled={savingEdit} className="bg-orange-600 hover:bg-orange-700" data-testid="notebook-edit-save">
              {savingEdit ? 'Saving…' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Protected audit trail */}
      <Dialog open={!!auditTarget} onOpenChange={(o) => !o && setAuditTarget(null)}>
        <DialogContent className="max-w-lg" data-testid="notebook-audit-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-sky-400" /> Technical Audit Trail</DialogTitle>
            <DialogDescription>Protected background record — not shown in the normal notebook. Immutable.</DialogDescription>
          </DialogHeader>
          {loadingAudit ? (
            <p className="text-sm text-muted-foreground py-6 text-center">Loading…</p>
          ) : auditData ? (
            <div className="space-y-4 max-h-[60vh] overflow-y-auto">
              <div className="rounded-lg bg-secondary/30 p-3 text-xs space-y-1">
                <p><span className="text-muted-foreground">Entry type:</span> {auditData.note.entry_type}{auditData.note.historic_date ? ` (historic date ${auditData.note.historic_date})` : ''}</p>
                <p><span className="text-muted-foreground">Actual system-entry:</span> <span data-testid="audit-system-created">{formatWhen(auditData.note.system_created_at)}</span></p>
                <p><span className="text-muted-foreground">Created by:</span> {auditData.note.created_by_name}</p>
                <p><span className="text-muted-foreground">Edited:</span> {auditData.note.is_edited ? 'Yes' : 'No'} {auditData.note.deleted ? '· Withdrawn' : ''}</p>
              </div>
              <div className="space-y-2">
                {auditData.audit_trail.map((a) => (
                  <div key={a.id} className="border-l-2 border-sky-500/40 pl-3 py-1 text-xs">
                    <p className="font-semibold capitalize text-foreground">{a.action} <span className="font-normal text-muted-foreground">— {formatWhen(a.system_timestamp)} by {a.performed_by_name}</span></p>
                    {a.action === 'edited' && (
                      <div className="mt-1 space-y-1">
                        <p className="text-red-300/80">Previous: {a.previous_content}</p>
                        <p className="text-emerald-300/80">New: {a.new_content}</p>
                        {a.edit_reason && <p className="text-muted-foreground">Reason: {a.edit_reason}</p>}
                      </div>
                    )}
                    {a.action === 'created' && a.content && <p className="text-muted-foreground mt-1">{a.content}</p>}
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground py-6 text-center">No audit data.</p>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAuditTarget(null)} data-testid="notebook-audit-close">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Raise disciplinary case from a behaviour-concern note */}
      <Dialog open={!!caseNote} onOpenChange={(o) => !o && setCaseNote(null)}>
        <DialogContent data-testid="notebook-case-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Scale className="w-5 h-5 text-amber-400" /> Open Disciplinary Case</DialogTitle>
            <DialogDescription>
              Create a disciplinary case for {selected?.full_name} from this behaviour-concern note. The case will be linked back to the note.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label>Case Type</Label>
              <Select value={caseForm.case_type} onValueChange={(v) => setCaseForm((f) => ({ ...f, case_type: v }))}>
                <SelectTrigger data-testid="notebook-case-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Verbal Warning">Verbal Warning</SelectItem>
                  <SelectItem value="Written Warning">Written Warning</SelectItem>
                  <SelectItem value="Final Warning">Final Warning</SelectItem>
                  <SelectItem value="Hearing">Hearing</SelectItem>
                  <SelectItem value="Suspension">Suspension</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Description</Label>
              <Textarea rows={4} value={caseForm.description} onChange={(e) => setCaseForm((f) => ({ ...f, description: e.target.value }))} data-testid="notebook-case-description" />
              <p className="text-xs text-muted-foreground">Pre-filled from the note — edit as needed. Opens on the Disciplinary page as an Open case.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCaseNote(null)} disabled={creatingCase}>Cancel</Button>
            <Button onClick={handleCreateCase} disabled={creatingCase} className="bg-amber-600 hover:bg-amber-700" data-testid="notebook-case-submit">
              {creatingCase ? 'Opening…' : 'Open Case'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
